import React, { useCallback, useContext, useState } from "react";
import { ActionType, CompleteLocationAvailabilityStepPayload, ReservationCreatedLocationAvailabilityStep } from "../../AppDataProvider/actions";
import { AppContext, BookingData, Organisation, OrganisationPage, TimeSlot } from "../../AppDataProvider/AppDataProvider";
import { media, paddingBottom, paddingLeft, paddingRight, paddingTop, paddingX } from "../../common/styles";
import { CreateReservationMutationVariables, useCreateReservationMutation } from "../../GeneratedGraphQL";
import { getDateDropdownOptions, getReservationPartsInput, getTimeDropdownOptions, requireBookingDataFields } from "../../utils";
import { OnFoundLocationOpts, Search } from "./Search/Search";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Divider } from "../../common/components";
import Toggle from "../../common/components/Toggle/Toggle";
import { PartRequired } from "../../utils/types";
import { calculateOrganisationProximity } from "../Organisations/calculateOrganisationProximity/calculateOrganisationProximity";
import { filterAndSortOrganisations } from "../Organisations/filterAndSortOrganisations/filterAndSortOrganisations";
import { Map } from "../Organisations/Mapkit/Map/Map";
import { OnSelectOrganisationOpts } from "../Organisations/Mapkit/Marker/Marker";
import { OrganisationsList } from "./OrganisationsList/OrganisationsList";
export type Coordinates = {
  latitude: number;
  longitude: number;
};
export type OnSelectOrganisationTimeSlotOpts = {
  selectedOrganisation: Organisation;
  selectedTimeSlot: TimeSlot;
};
type LocationAvailabilityStepBookingData = PartRequired<BookingData, "createdSource" | "timezone">;
export function LocationAvailabilityStep() {
  const {
    t
  } = useTranslation();
  const {
    appDataDispatch,
    appData
  } = useContext(AppContext);
  requireBookingDataFields<LocationAvailabilityStepBookingData>(appData.bookingData, ["createdSource", "timezone"]);
  const {
    attendees,
    createdSource,
    duration,
    sessionId,
    timezone
  } = appData.bookingData;
  const {
    locationSelection,
    unitOfMeasurement,
    services
  } = appData.bookingTool;
  const urlParams = new URLSearchParams(window.location.search);
  const preFillDateTimeSearch = urlParams.get("dateTimeSearchPrefill");
  const prefilledDisplayDateCount = urlParams.get("displayDateCountPrefill");
  const prefilledLocationSearch = urlParams.get("locationSearchPrefill");
  const prefilledLatitude = urlParams.get("locationCoordinatesPrefill[latitude]");
  const prefilledLongitude = urlParams.get("locationCoordinatesPrefill[longitude]");
  const prefilledCoordinates = prefilledLatitude && prefilledLongitude ? {
    latitude: parseFloat(prefilledLatitude),
    longitude: parseFloat(prefilledLongitude)
  } : null;
  const prefilledOrganisationId = urlParams.get("organisationId") ?? undefined;
  const futureDaysTotal = prefilledDisplayDateCount && prefilledDisplayDateCount !== "" ? parseInt(prefilledDisplayDateCount, 10) : 14;
  const prefilledOrganisation = appData.bookingTool.organisations.edges.find(({
    node
  }) => node._id === prefilledOrganisationId);
  const noticePeriodInHours = prefilledOrganisation ? prefilledOrganisation.node.bookingNoticePeriod : 0;
  const dateDropdownOptions = getDateDropdownOptions({
    futureDaysTotal,
    noticePeriodInHours,
    t
  });
  const timeDropdownOptions = getTimeDropdownOptions("08:00", "18:30");
  const defaultDateSearch = dateDropdownOptions[0].optionId;
  const defaultTimeSearch = timeDropdownOptions[3].optionId;
  const initialSearchedDate = preFillDateTimeSearch ? preFillDateTimeSearch.split("T")[0] : defaultDateSearch;
  const initialSearchedTime = preFillDateTimeSearch ? preFillDateTimeSearch.split("T")[1] : defaultTimeSearch;

  /**
   * Attempt to retrieve cached set of coordinates from session storage
   * to avoid the user retyping this query.
   */
  const cachedCoordinatesStr = window.sessionStorage.getItem(`${sessionId}:location-search-query`);
  let cachedCoordinates = null;
  if (cachedCoordinatesStr !== null) {
    cachedCoordinates = (JSON.parse(cachedCoordinatesStr) as {
      searchedCoordinates: Coordinates;
      searchedString: string;
    });
  }
  const sessionLocation = cachedCoordinates?.searchedString;

  /**
   * Attempt to retrieve cached date and time the user searched for from session
   * storage to avoid the user having to re-select from dropdown.
   */
  const cachedSelectedDate = window.sessionStorage.getItem(`${sessionId}:selected-date`);
  const cachedSelectedTime = window.sessionStorage.getItem(`${sessionId}:selected-time`);
  const [selectedDate, setSelectedDate] = useState<string>(cachedSelectedDate ?? initialSearchedDate);
  const [selectedTime, setSelectedTime] = useState<string>(cachedSelectedTime ?? initialSearchedTime);
  const locationText = sessionLocation ?? prefilledLocationSearch ?? "";

  /**
   * Is used by the map to display a marker for where
   * the user's found search location is.
   *
   * Is also used as a point of comparison when calculating
   * the proximity between the users search location
   * and each organisation.
   */

  const [searchCoordinates, setSearchCoordinates] = useState<Coordinates | null>(prefilledCoordinates ?? cachedCoordinates?.searchedCoordinates ?? null);
  const [selectedOrganisationFromMap, setSelectedOrganisationFromMap] = useState<Organisation | null>(null);
  const selectOrganisation = (options: OnSelectOrganisationOpts) => {
    setSelectedOrganisationFromMap(options.organisation);
    if (displayMode === "map") {
      setDisplayMode("list");
    }
    const element = document.getElementById(options.organisation._id);
    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start"
      });
    }
  };
  const organisationsWithLocation = {
    ...appData.bookingTool.organisations,
    edges: appData.bookingTool.organisations.edges.filter(({
      node
    }) => {
      return node.location && node.location.latitude && node.location.longitude;
    })
  };
  const [organisationList, setOrganisationList] = useState<OrganisationPage>(filterAndSortOrganisations({
    ...organisationsWithLocation,
    edges: organisationsWithLocation.edges.map(({
      node,
      cursor
    }) => {
      const organisation = node;
      /**
       * We cast the location object to include coordinate values
       * as we know that organisations without a location are
       * filtered out prior to this code being run.
       */
      const location = (organisation.location as {
        latitude: string;
        longitude: string;
      });
      const organisationCoordinates = {
        latitude: parseFloat(location.latitude),
        longitude: parseFloat(location.longitude)
      };
      if (location && searchCoordinates) {
        const proximityToSearchedLocation = calculateOrganisationProximity(searchCoordinates, organisationCoordinates, {
          unitOfMeasurement
        });
        return {
          cursor,
          node: {
            ...organisation,
            proximityToSearchedLocation
          }
        };
      } else {
        return {
          cursor,
          node: {
            ...organisation
          }
        };
      }
    })
  }, locationSelection));
  const [locationError, setLocationError] = useState(false);
  const [displayMode, setDisplayMode] = useState<"list" | "map">("list");
  const [inertTimeSlots, setInertTimeSlots] = useState(false);
  const isTimeSlotsInert = useCallback(() => {
    if (prefilledOrganisationId) {
      return false;
    }
    return inertTimeSlots;
  }, [inertTimeSlots, prefilledOrganisationId]);
  const handleLocationError = (hasError: boolean) => {
    setLocationError(hasError);
    if (hasError) {
      setSearchCoordinates(null);
    }
  };
  const handleSearch = (date: string, time: string, location?: OnFoundLocationOpts) => {
    setSelectedDate(date);
    setSelectedTime(time);
    const searchedCoordinates = location?.coordinates;
    const searchedString = location?.searchStr;
    window.sessionStorage.setItem(`${sessionId}:location-search-query`, JSON.stringify({
      searchedCoordinates,
      searchedString
    }));
    window.sessionStorage.setItem(`${sessionId}:selected-date`, date);
    window.sessionStorage.setItem(`${sessionId}:selected-time`, time);
    const organisations = filterAndSortOrganisations({
      ...organisationsWithLocation,
      edges: organisationsWithLocation.edges.map(({
        node,
        cursor
      }) => {
        const organisation = node;
        /**
         * We cast the location object to include coordinate values
         * as we know that organisations without a location are
         * filtered out prior to this code being run.
         */
        const location = (organisation.location as {
          latitude: string;
          longitude: string;
        });
        const organisationCoordinates = {
          latitude: parseFloat(location.latitude),
          longitude: parseFloat(location.longitude)
        };
        if (location && searchedCoordinates) {
          const proximityToSearchedLocation = calculateOrganisationProximity(searchedCoordinates, organisationCoordinates, {
            unitOfMeasurement
          });
          return {
            cursor,
            node: {
              ...organisation,
              proximityToSearchedLocation
            }
          };
        } else {
          return {
            cursor,
            node: {
              ...organisation
            }
          };
        }
      })
    }, locationSelection);
    window.sessionStorage.setItem(`${appData.bookingData.sessionId}:location-search-query`, JSON.stringify({
      searchedCoordinates,
      searchedString
    }));
    if (location && searchedCoordinates) {
      setSearchCoordinates(searchedCoordinates);
    }
    setOrganisationList(organisations);
    setInertTimeSlots(false);
  };

  // create reservation and go to next step when selecting a timeslot for an organisation
  const [createReservation] = useCreateReservationMutation({
    errorPolicy: "all"
  });
  const [createReservationLoading, setCreateReservationLoading] = useState<boolean>(false);
  const createStandardReservation = async (options: OnSelectOrganisationTimeSlotOpts) => {
    const {
      selectedOrganisation,
      selectedTimeSlot
    } = options;
    const {
      interval
    } = selectedTimeSlot;

    /**
     * Add 1 minute to reservation expiry on the back end.
     * This buffer means front-end will prompt for a reload 1 minute before
     * actual expiry, avoiding situations where user is interacting at same
     * time as reservation expiry.
     * Chronos only accepts full-minute values so 1 minute is easiest compromise
     */
    const {
      reservationExpiryMinutes,
      defaultTimeZone
    } = selectedOrganisation;
    const expiresAfter = reservationExpiryMinutes + 1;

    // For now, there will only be one service for each organisation.
    const selectedService = services.edges.find(({
      node
    }) => node.organisation._id === selectedOrganisation._id);
    if (!selectedService) {
      console.error("No service found for the selected organisation");
      return;
    }
    const {
      parts,
      durations,
      _id
    } = selectedService.node;
    const variables: CreateReservationMutationVariables = {
      bookingToolId: appData.bookingTool._id,
      expiresAfter,
      ...(interval.__typename === "AvailableInterval" && {
        intervalResourceIds: interval.resourceIds
      }),
      parts: getReservationPartsInput({
        parts,
        selectedInterval: interval
      }),
      serviceId: _id,
      source: createdSource,
      timeZone: defaultTimeZone || timezone
    };
    const {
      data,
      errors
    } = await createReservation({
      variables
    });
    if (errors?.some(error => error.message === "Response code 422 (Unprocessable Entity)")) {
      appDataDispatch({
        payload: {
          buttonLabel: t("reload"),
          content: t("errors.somethingChanged"),
          icon: faExclamationTriangle,
          onClick: /* istanbul ignore next */() => location.reload(),
          title: t("somethingChanged")
        },
        type: ActionType.SHOW_MODAL_DIALOG
      });
      return;
    } else if (!data || errors) {
      appDataDispatch({
        payload: {
          message: t("errors.cannotCreateReservation"),
          variant: "warning"
        },
        type: ActionType.ADD_TOAST
      });
      setCreateReservationLoading(false);
      return;
    }
    const {
      reservedSpace,
      reservation
    } = data.createReservation;
    setCreateReservationLoading(false);
    const resourceIds = interval.__typename === "AvailableInterval" ? interval.resourceIds : [];

    // For now, we are not supporting group or recurring bookings
    const updatedData: ReservationCreatedLocationAvailabilityStep = {
      attendees: attendees ?? 1,
      duration: duration ? duration : durations.find(duration => duration.default) ?? durations[0],
      organisation: selectedOrganisation,
      reservedSpaceId: reservedSpace._id,
      ...(resourceIds && {
        resourceIds: resourceIds
      }),
      service: selectedService.node,
      startDate: new Date(reservation.start),
      timezone: defaultTimeZone || timezone
    };
    const payload: CompleteLocationAvailabilityStepPayload = {
      updatedData
    };
    appDataDispatch({
      payload,
      type: ActionType.COMPLETE_LOCATION_AVAILABILITY_STEP
    });
  };
  const completeStep = (options: OnSelectOrganisationTimeSlotOpts) => {
    const {
      selectedOrganisation,
      selectedTimeSlot
    } = options;
    setCreateReservationLoading(true);
    void createStandardReservation({
      selectedOrganisation,
      selectedTimeSlot
    });
  };
  const showOrganisationList = prefilledOrganisationId || organisationList.edges.length > 0 && !locationError;
  return <Container displayMode={displayMode} data-sentry-element="Container" data-sentry-component="LocationAvailabilityStep" data-sentry-source-file="LocationAvailabilityStep.tsx">
      <SearchListContainer prefilledOrganisationId={prefilledOrganisationId} data-sentry-element="SearchListContainer" data-sentry-source-file="LocationAvailabilityStep.tsx">
        <Search prefilledOrganisationId={prefilledOrganisationId} onChange={() => {
        setInertTimeSlots(true);
      }} onLocationError={handleLocationError} onSearch={handleSearch} selectedDate={selectedDate} selectedTime={selectedTime} selectedLocationText={locationText} selectedCoordinates={searchCoordinates} dateDropdownOptions={dateDropdownOptions} timeDropdownOptions={timeDropdownOptions} data-sentry-element="Search" data-sentry-source-file="LocationAvailabilityStep.tsx" />
        {!prefilledOrganisationId && <ToggleContainer>
            <Toggle options={[{
          label: t("list"),
          value: "list"
        }, {
          label: t("map"),
          value: "map"
        }]} selectedValue={displayMode} onToggle={value => setDisplayMode((value as "list" | "map"))} />
          </ToggleContainer>}
        <OrganisationsContainer displayMode={displayMode} inert={inertTimeSlots}
      /**
       * We need to set the inert attribute via a ref or it won't actually
       * get set on the element.
       */ ref={e => isTimeSlotsInert() ? e?.setAttribute("inert", "") : e?.removeAttribute("inert")} data-sentry-element="OrganisationsContainer" data-sentry-source-file="LocationAvailabilityStep.tsx">
          {showOrganisationList ? <OrganisationsList createReservationLoading={createReservationLoading} filteredOrganisations={organisationList} queryByOrganisationTimeZone selectedDate={selectedDate} selectedTime={selectedTime} onSelectOrganisationTimeSlot={completeStep} selectedOrganisationFromMap={selectedOrganisationFromMap} prefilledOrganisationId={prefilledOrganisationId} /> : <NoOrganisationsFound>{t("locationNotFound")}</NoOrganisationsFound>}
        </OrganisationsContainer>
      </SearchListContainer>
      {!prefilledOrganisationId && showOrganisationList && <VerticalDivider />}
      {!prefilledOrganisationId && <MapContainer displayMode={displayMode} inert={inertTimeSlots}
    /**
     * We need to set the inert attribute via a ref or it won't actually
     * get set on the element.
     */ ref={e => isTimeSlotsInert() ? e?.setAttribute("inert", "") : e?.removeAttribute("inert")}>
          {searchCoordinates && showOrganisationList && <Map organisations={organisationList} searchCoordinates={searchCoordinates} onSelectOrganisation={selectOrganisation} highlightOrgCardOnMapPinSelect />}
          {!showOrganisationList && displayMode === "map" && <NoLocationFound>{t("locationNotFound")}</NoLocationFound>}
        </MapContainer>}
    </Container>;
}
const Container = styled.div<{
  displayMode: "list" | "map";
}>`
  display: flex;
  justify-content: center;
  align-self: center;
  width: 100%;
  height: 100%;
  flex-direction: ${({
  displayMode
}) => displayMode === "map" ? "column" : "row"};
  ${paddingX.m};

  @media ${media.desktop} {
    flex-direction: row;
  }
`;
const SearchListContainer = styled.div<{
  prefilledOrganisationId?: string;
}>`
  display: flex;
  flex-direction: column;
  width: 100%;

  @media ${media.desktop} {
    width: ${({
  prefilledOrganisationId
}) => prefilledOrganisationId ? "100%" : "40%"};
    padding-left: ${({
  prefilledOrganisationId
}) => prefilledOrganisationId ? `${paddingRight.zero}` : `${paddingRight.m}`};
  }
`;
const ToggleContainer = styled.div`
  display: flex;
  width: 100%;
  ${paddingTop.s}
  ${paddingBottom.m}
  align-items: center;
  justify-content: flex-end;

  @media ${media.desktop} {
    display: none;
  }
`;
const OrganisationsContainer = styled.div<{
  displayMode: "list" | "map";
  inert?: boolean;
}>`
  width: 100%;
  justify-content: center;
  align-items: flex-start;
  flex-direction: column;
  display: ${({
  displayMode
}) => displayMode === "list" ? "flex" : "none"};
  opacity: ${({
  inert
}) => inert ? 0.2 : 1};

  @media ${media.desktop} {
    display: block;
    overflow: auto;
    max-height: 474px;
    scrollbar-width: none;
  }

  ::-webkit-scrollbar {
    display: none;
  }
`;
const VerticalDivider = styled(Divider).attrs({
  vertical: true
})`
  display: none;

  @media ${media.desktop} {
    display: flex;
  }
`;
const NoOrganisationsFound = styled.div`
  display: flex;
  width: 100%;
`;
const NoLocationFound = styled.div`
  display: flex;
  width: 100%;

  @media ${media.desktop} {
    display: none;
  }
`;
const MapContainer = styled.div<{
  displayMode: "list" | "map";
  inert?: boolean;
}>`
  height: 694px;
  width: 100%;
  flex-direction: row;
  display: ${({
  displayMode
}) => displayMode === "map" ? "flex" : "none"};
  opacity: ${({
  inert
}) => inert ? 0.2 : 1};

  @media ${media.desktop} {
    display: flex;
    width: 60%;
    ${paddingLeft.m}
  }
`;